import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Upload, File, Download } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Opportunity, DEFAULT_INDUSTRY_AREAS, FileAttachment } from '../types';
import { calculatePriorityScore } from '../utils/priorityScore';
import { uploadFile, deleteFile } from '../firebase/firestore';
import GoogleDrivePicker from './GoogleDrivePicker';
import { GoogleDriveFile } from '../services/googlePicker';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { googlePickerService } from '../services/googlePicker';

interface OpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (opportunity: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onDelete?: (opportunityId: string) => void;
  opportunity?: Opportunity;
  columnId: string;
  boardId: string;
  userId: string;
}

const OpportunityModal: React.FC<OpportunityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  opportunity,
  columnId,
  boardId,
  userId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [effortLevel, setEffortLevel] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [status, setStatus] = useState<'Prospecting' | 'Qualified' | 'Proposal' | 'Negotiation' | 'Closed Won' | 'Closed Lost'>('Prospecting');
  const [opportunityType, setOpportunityType] = useState<'RFP' | 'Service Contract' | 'Partnership' | 'Consulting' | 'Training' | 'Support' | 'Other'>('RFP');
  const [clientSize, setClientSize] = useState<'Startup' | 'SMB' | 'Enterprise' | 'Government' | 'Non-Profit'>('SMB');
  const [estimatedValue, setEstimatedValue] = useState<number>(0);
  const [winProbability, setWinProbability] = useState<number>(50);
  const [competitors, setCompetitors] = useState<string[]>(['']);
  const [businessModel, setBusinessModel] = useState<'SaaS' | 'Marketplace' | 'E-commerce' | 'Mobile App' | 'API/Platform' | 'Hardware' | 'Consulting' | 'Other'>('Consulting');
  const [supportOverhead, setSupportOverhead] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [industryAreas, setIndustryAreas] = useState<string[]>([]);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGoogleDrivePickerOpen, setIsGoogleDrivePickerOpen] = useState(false);
  const [isDownloadingFromDrive, setIsDownloadingFromDrive] = useState(false);

  // Load opportunity data when editing
  useEffect(() => {
    if (opportunity) {
      setName(opportunity.name);
      setDescription(opportunity.description || '');
      setAiSummary(opportunity.aiSummary || '');
      setEffortLevel(opportunity.effortLevel);
      setStatus(opportunity.status || 'Prospecting');
      setOpportunityType(opportunity.opportunityType || 'RFP');
      setClientSize(opportunity.clientSize || 'SMB');
      setEstimatedValue(opportunity.estimatedValue || 0);
      setWinProbability(opportunity.winProbability || 50);
      setCompetitors(opportunity.competitors && opportunity.competitors.length > 0 ? opportunity.competitors : ['']);
      setBusinessModel(opportunity.businessModel || 'Consulting');
      setSupportOverhead(opportunity.supportOverhead || 'Medium');
      setIndustryAreas(opportunity.industryAreas || []);
      setAttachments(opportunity.attachments || []);
    } else {
      // Reset form for new opportunity
      setName('');
      setDescription('');
      setAiSummary('');
      setEffortLevel('Medium');
      setStatus('Prospecting');
      setOpportunityType('RFP');
      setClientSize('SMB');
      setEstimatedValue(0);
      setWinProbability(50);
      setCompetitors(['']);
      setBusinessModel('Consulting');
      setSupportOverhead('Medium');
      setIndustryAreas([]);
      setAttachments([]);
    }
  }, [opportunity, isOpen]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    setIsSubmitting(true);
    
    try {
      const opportunityData: any = {
        name: name.trim(),
        effortLevel,
        status,
        opportunityType,
        clientSize,
        estimatedValue,
        winProbability,
        competitors: competitors.filter(c => c && c.trim() !== ''),
        businessModel,
        supportOverhead,
        industryAreas,
        columnId,
        boardId,
        createdBy: userId,
      };

      if (description.trim()) {
        opportunityData.description = description.trim();
      }
      if (aiSummary.trim()) {
        opportunityData.aiSummary = aiSummary.trim();
      }
      if (attachments.length > 0) {
        opportunityData.attachments = attachments;
      }

      // Calculate priority score
      const priorityScore = calculatePriorityScore(opportunityData as Opportunity);
      opportunityData.priorityScore = priorityScore;

      await onSave(opportunityData);
      onClose();
    } catch (error) {
      console.error('Error saving opportunity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCompetitor = () => {
    setCompetitors([...competitors, '']);
  };

  const removeCompetitor = (index: number) => {
    if (competitors.length > 1) {
      setCompetitors(competitors.filter((_, i) => i !== index));
    }
  };

  const updateCompetitor = (index: number, value: string) => {
    const newCompetitors = [...competitors];
    newCompetitors[index] = value;
    setCompetitors(newCompetitors);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const attachment = await uploadFile(file, opportunity?.id || 'temp', userId);
        setAttachments(prev => [...prev, attachment]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDelete = async (attachmentId: string) => {
    try {
      const attachment = attachments.find(a => a.id === attachmentId);
      if (attachment) {
        await deleteFile(attachment.url, attachment.storagePath);
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!opportunity || !onDelete) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${opportunity.name}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setIsDeleting(true);
    try {
      await onDelete(opportunity.id);
      onClose();
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      alert('Failed to delete opportunity. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('presentation')) return '📈';
    return '📎';
  };

  const handleGoogleDriveFileSelect = async (files: GoogleDriveFile[]) => {
    setIsDownloadingFromDrive(true);
    
    try {
      const functions = getFunctions();
      const downloadFromGoogleDrive = httpsCallable(functions, 'downloadFromGoogleDrive');
      
      for (const file of files) {
        try {
          // Get the access token from the Google Picker service
          const accessToken = await googlePickerService.authenticate();
          
          // Download file from Google Drive via Cloud Function
          const result = await downloadFromGoogleDrive({
            fileId: file.id,
            accessToken: accessToken
          });
          
          const { data, contentType } = result.data as any;
          
          // Convert base64 back to ArrayBuffer
          const binaryString = atob(data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create File object
          const fileBlob = new Blob([bytes], { type: contentType });
          const fileObject = Object.assign(fileBlob, { name: file.name }) as File;
          
          // Upload to Firebase Storage using existing logic
          const opportunityId = opportunity ? opportunity.id : 'temp';
          const attachment = await uploadFile(fileObject, opportunityId, userId);
          setAttachments(prev => [...prev, attachment]);

        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          alert(`Failed to process ${file.name}. Please try again.`);
        }
      }
    } catch (error) {
      console.error('Error downloading from Google Drive:', error);
      alert('Failed to download files from Google Drive. Please try again.');
    } finally {
      setIsDownloadingFromDrive(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="glass-effect rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {opportunity ? 'Edit Opportunity' : 'Add New Opportunity'}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-field w-full"
                    placeholder="Enter opportunity name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="input-field w-full resize-none"
                    rows={3}
                    placeholder="Describe the opportunity"
                  />
                </div>

                {/* Opportunity Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opportunity Type
                    </label>
                    <select
                      value={opportunityType}
                      onChange={(e) => setOpportunityType(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="RFP">RFP</option>
                      <option value="Service Contract">Service Contract</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Consulting">Consulting</option>
                      <option value="Training">Training</option>
                      <option value="Support">Support</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Size
                    </label>
                    <select
                      value={clientSize}
                      onChange={(e) => setClientSize(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="Startup">Startup</option>
                      <option value="SMB">SMB</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="Government">Government</option>
                      <option value="Non-Profit">Non-Profit</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Value ($)
                    </label>
                    <input
                      type="number"
                      value={estimatedValue}
                      onChange={(e) => setEstimatedValue(Number(e.target.value))}
                      className="input-field w-full"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Win Probability (%)
                    </label>
                    <input
                      type="number"
                      value={winProbability}
                      onChange={(e) => setWinProbability(Number(e.target.value))}
                      className="input-field w-full"
                      placeholder="50"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                {/* Status and Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="Prospecting">Prospecting</option>
                      <option value="Qualified">Qualified</option>
                      <option value="Proposal">Proposal</option>
                      <option value="Negotiation">Negotiation</option>
                      <option value="Closed Won">Closed Won</option>
                      <option value="Closed Lost">Closed Lost</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Effort Level
                    </label>
                    <select
                      value={effortLevel}
                      onChange={(e) => setEffortLevel(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>

                </div>

                {/* Business Model and Support */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Model
                    </label>
                    <select
                      value={businessModel}
                      onChange={(e) => setBusinessModel(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="SaaS">SaaS</option>
                      <option value="API/Platform">API/Platform</option>
                      <option value="Marketplace">Marketplace</option>
                      <option value="Mobile App">Mobile App</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Hardware">Hardware</option>
                      <option value="Consulting">Consulting</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Overhead
                    </label>
                    <select
                      value={supportOverhead}
                      onChange={(e) => setSupportOverhead(e.target.value as any)}
                      className="input-field w-full"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Difficulty of customer support
                    </p>
                  </div>
                </div>

                {/* Industry Areas - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Areas
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-xl p-3 bg-white/15">
                    <div className="grid grid-cols-3 gap-3">
                      {DEFAULT_INDUSTRY_AREAS.map((industry) => (
                        <label key={industry} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={industryAreas.includes(industry)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIndustryAreas([...industryAreas, industry]);
                              } else {
                                setIndustryAreas(industryAreas.filter(area => area !== industry));
                              }
                            }}
                            className="rounded border-gray-300 text-satsuma-600 focus:ring-satsuma-500"
                          />
                          <span className="text-sm text-gray-700">{industry}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select all relevant industries this opportunity affects
                  </p>
                </div>

                {/* Competitors */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Competitors
                    </label>
                  </div>
                  <div className="space-y-2">
                    {competitors.map((competitor, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={competitor}
                          onChange={(e) => updateCompetitor(index, e.target.value)}
                          className="input-field flex-1"
                          placeholder="Enter a competitor"
                        />
                        {competitors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCompetitor(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCompetitor}
                      className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2"
                    >
                      <Plus size={16} />
                      <span>Add Competitor</span>
                    </button>
                  </div>
                </div>

                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Attachments
                  </label>
                  <div className="space-y-3">
                    {/* File Upload */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                      />
                      <label
                        htmlFor="file-upload"
                        className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2 cursor-pointer"
                      >
                        <Upload size={16} />
                        <span>{isUploading ? 'Uploading...' : 'Upload Files'}</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsGoogleDrivePickerOpen(true)}
                        disabled={isDownloadingFromDrive}
                        className="btn-secondary text-sm py-2 px-3 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download size={16} />
                        <span>{isDownloadingFromDrive ? 'Downloading...' : 'From Google Drive'}</span>
                      </button>
                      <span className="text-xs text-gray-500">
                        PDF, Word, Excel, PowerPoint, Images
                      </span>
                    </div>
                    
                    {/* File List */}
                    {attachments.length > 0 && (
                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center space-x-3">
                              <span className="text-lg">{getFileIcon(attachment.type)}</span>
                              <div>
                                <p className="text-sm font-medium text-gray-800">
                                  {attachment.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(attachment.size)} • {new Date(attachment.uploadedAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="Download file"
                              >
                                <Download size={16} />
                              </a>
                              <button
                                onClick={() => handleFileDelete(attachment.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Delete file"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4">
                  {/* Delete Button - Only show for existing opportunities */}
                  {opportunity && onDelete && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          <span>Delete Opportunity</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Save/Cancel Buttons */}
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </div>
                      ) : (
                        opportunity ? 'Update Opportunity' : 'Create Opportunity'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Google Drive Picker Modal */}
      <GoogleDrivePicker
        isOpen={isGoogleDrivePickerOpen}
        onClose={() => setIsGoogleDrivePickerOpen(false)}
        onFileSelect={handleGoogleDriveFileSelect}
      />
    </>
  );
};

export default OpportunityModal;
